/**
 * Dialog "Forced Close" — bám `form_frmCtrlSalesQryLocalS_CloseAdd.txt`.
 *
 * Access caption form: "Sales Controlsheet - Forced Close Confirmation";
 * header label `Auto_Header0` = "Finalized Confirmation".
 * FormHeader btnSave (caption "Forced Close") + btnCancel → đặt top-right DialogTitle.
 * Detail (sort theo Top): `contract_sale` (readonly), `pending` (readonly, General
 * Number, DefaultValue=[pending_bag]), `finalized_date` (required, DatePicker,
 * DefaultValue=Now()). VBA `btnSave_Click` validate `finalized_date != ''` →
 * UPDATE `public_local_s`.
 */
import dayjs, { type Dayjs } from 'dayjs'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { useState } from 'react'

import { useSnackbar } from '../../hooks/useSnackbar'
import { useForcedCloseLocalSMutation } from '../../store/api/saleControlApi'
import { formatAccessNumber } from './saleControlAccessUi'

/** Format `pending` (Pending Kg) khớp cell `kind:'number'` của list (thousand sep + 2 decimals nếu lẻ). */
function formatPending(v: number | string | null): string {
  if (v == null || v === '') return ''
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''))
  return Number.isFinite(n) ? formatAccessNumber(n) : String(v)
}

const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const readonlyFieldSx = {
  '& .MuiInputBase-root': { bgcolor: (t: Theme) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f1f3f5') },
  '& .MuiInputBase-input': { color: (t: Theme) => t.palette.text.secondary, fontWeight: 600 },
}

export interface SaleControlLocalSCloseDialogProps {
  open: boolean
  contractSale: string
  pending: number | string | null
  onClose: () => void
}

export default function SaleControlLocalSCloseDialog({
  open,
  contractSale,
  pending,
  onClose,
}: SaleControlLocalSCloseDialogProps) {
  const snack = useSnackbar()
  // Mount mới mỗi lần `open` đổi (key trên Dialog gốc) → date reset về today (DefaultValue=Now()).
  const [finalizedDate, setFinalizedDate] = useState<Dayjs | null>(() => dayjs())
  const [forcedClose, { isLoading: saving }] = useForcedCloseLocalSMutation()

  const canSave = !!finalizedDate && finalizedDate.isValid() && !saving

  // btnSave_Click: validate finalized_date → UPDATE public_local_s → parent Requery (RTK invalidate).
  const handleSave = async () => {
    if (!canSave) return
    try {
      await forcedClose({
        contractSale,
        finalizedDate: finalizedDate!.format('YYYY-MM-DD'),
      }).unwrap()
      snack.success('Successfully Forced')
      onClose()
    } catch (e: unknown) {
      snack.error(e instanceof Error ? e.message : 'Forced close failed.')
    }
  }

  // btnCancel_Click: Undo + Close (draft là local state → unmount tự xoá).
  const handleClose = () => {
    if (saving) return
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (saving || reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        Finalized Confirmation
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button type="button" variant="contained" color="error" sx={titleBtnSx} onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Forced Close'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} onClick={handleClose} disabled={saving}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography variant="body2">
            Do you want to forced close this contract with the following information?
          </Typography>
          <TextField
            label="Contract No"
            value={contractSale}
            size="small"
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: -1 }}
            sx={readonlyFieldSx}
          />
          <TextField
            label="Pending"
            value={formatPending(pending)}
            size="small"
            InputProps={{ readOnly: true }}
            InputLabelProps={{ shrink: true }}
            inputProps={{ tabIndex: -1, style: { textAlign: 'right' } }}
            sx={readonlyFieldSx}
          />
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Finalized Date"
              format="DD-MMM-YYYY"
              value={finalizedDate}
              onChange={(d) => setFinalizedDate(d)}
              slotProps={{ textField: { size: 'small', required: true, inputProps: { tabIndex: 1 } } }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
    </Dialog>
  )
}
